import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import { TextInput, Alert } from 'flowbite-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { ChevronLeft } from 'lucide-react';

const FinalizePasswordReset = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validatingCode, setValidatingCode] = useState(true);
  const [actionCode, setActionCode] = useState('');
  const [email, setEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  
  // Get the action code and verify it on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const oobCode = urlParams.get('oobCode');
    
    if (!oobCode) {
      setError("Invalid password reset link. Please request a new link.");
      setValidatingCode(false);
      return;
    }
    
    setActionCode(oobCode);
    
    // Verify the action code is valid
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setEmail(email); // Save the email for display
        setValidatingCode(false);
      })
      .catch((error) => {
        console.error("Error verifying reset code:", error);
        setError("This password reset link is invalid or has expired. Please request a new link.");
        setValidatingCode(false);
      });
  }, [location]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    if (newPassword.length < 6) {
      setError("Password should be at least 6 characters");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setSuccess(true);
    } catch (error) {
      console.error("Error confirming password reset:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if we're in the native app environment
  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  
  return (
    <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {/* Background styling */}
      {isNative ? (
        <>
          {/* Background Image with Gradient Overlay - Native App */}
          <div className="absolute inset-0">
            <img
              src="/images/headerbg.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className={`absolute inset-0 ${
              isDarkMode
                ? 'bg-gradient-to-b from-emerald-900/95 via-[#111111]/98 to-[#111111]'
                : 'bg-gradient-to-b from-emerald-600/95 via-white/98 to-white'
            }`} />
          </div>

          {/* Gradient Orbs - Native App */}
          <div className={`absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-200/40'
          }`} />
          <div className={`absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-100/60'
          }`} />
        </>
      ) : (
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
            : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
        }`} />
      )}

      {/* Back Button */}
      <div className="absolute top-0 left-0 z-10 m-4" style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top))' : '0' }}>
        <Link
          to="/login"
          className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
            isDarkMode 
              ? 'bg-white/10 hover:bg-white/20 text-white' 
              : 'bg-black/10 hover:bg-black/20 text-black'
          }`}
        >
          <ChevronLeft size={20} />
        </Link>
      </div>
      
      <div className="relative flex-1 flex items-center justify-center px-4" 
           style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top) + 2rem)' : '2rem' }}>
        <div className={`w-full max-w-md ${
          isNative ? 'bg-transparent' : `rounded-[24px] overflow-hidden ${
            isDarkMode 
              ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
              : 'bg-white shadow-sm border border-gray-200'
          }`
        }`}>
          <div className="p-8">
            {/* Logo - Only show in native app */}
            {isNative && (
              <div className="flex justify-center mb-12">
                <div className="w-24 h-24">
                  <img
                    src="/images/ummahti_white_transparent.png"
                    alt="Ummahti"
                    className={`w-full transition-opacity duration-300 ${isDarkMode ? 'opacity-90' : 'opacity-80'}`}
                  />
                </div>
              </div>
            )}
            
            <h2 className={`text-2xl font-light mb-4 text-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {validatingCode ? 'Verifying your link...' : 
                success ? 'Password Changed' : 'Create New Password'}
            </h2>
            
            {!validatingCode && !success && email && (
              <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Set a new password for<br/>
                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {email}
                </span>
              </p>
            )}
            
            {validatingCode && (
              <div className="flex justify-center my-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            )}
            
            {error && (
              <Alert 
                color="failure" 
                className="mb-6"
                onDismiss={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            
            {success ? (
              <div className="text-center">
                <Alert 
                  color="success" 
                  className="mb-6"
                >
                  Your password has been successfully reset.
                </Alert>
                <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  You can now log in with your new password.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/login')}
                    className={`w-full py-4 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            ) : !validatingCode && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <TextInput
                    type="password"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className={`${isDarkMode ? '[&>input]:bg-[#1A1A1A] [&>input]:border-gray-800' : ''} ${
                      isNative ? '[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md' : ''
                    }`}
                  />
                </div>
                <div>
                  <TextInput
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`${isDarkMode ? '[&>input]:bg-[#1A1A1A] [&>input]:border-gray-800' : ''} ${
                      isNative ? '[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md' : ''
                    }`}
                  />
                </div>
                <button 
                  type="submit" 
                  className={`w-full py-4 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer Text - Only show in native app */}
      {isNative && (
        <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      )}
    </div>
  );
};

export default FinalizePasswordReset; 